<script setup lang="ts">
import type { ReferenceDetail } from '~/types/api'

const localePath = useLocalePath()
const route = useRoute()
const { trimDescription, addSuffix } = useMeta()
const { slug } = route.params

const { t, te } = useI18n()

const { getResourceUrl } = useResources()

const referencesRepository = createReferencesRepository()
const { data: reference } = await useAsyncData(() =>
  referencesRepository.getBySlug(slug).catch(() => {
    navigateTo(localePath({ name: 'references' }))
  })
)

if (reference.value?.translationOfNid) {
  await useTranslatedSlugs(reference.value?.translationOfNid)
}

const { defaultOgImage } = useOgImage()

useSeoMeta({
  title: addSuffix(reference?.value?.title) ?? t('pages.references.meta.title'),
  description:
    trimDescription(stripHtml(reference?.value?.content)) ??
    (te('pages.references.meta.description')
      ? t('pages.references.meta.description')
      : trimDescription(t('pages.references.description'))),
  ogImage: reference.value?.photos[0]?.url ?? defaultOgImage,
})

const subtitle = computed(() => {
  const values = [
    reference.value?.year,
    reference.value?.location,
    reference.value?.country?.title,
  ]
  return values.filter((val) => !!val).join(' — ')
})

const { data: moreReferences, pending: loadingMore } = await useAsyncData(
  slug + '-more-references',
  () => referencesRepository.getMoreReferences(reference.value.nid),
  {
    default: defaultPaginationObject,
  }
)

const productImages = computed(() => {
  return (reference.value?.products || []).reduce(
    (acc: ReferenceDetail['products'][], product) => {
      if (!acc.find((p) => p.product.nid === product.translationOfNid)) {
        acc.push({
          image: product?.images?.[0] || '',
          product,
          productLine: product.productLine,
        })
      }
      return acc
    },
    []
  )
})
</script>

<template>
  <main>
    <UiBanner
      :title="reference?.title ?? ''"
      :description="subtitle"
      :slug="reference?.title ?? undefined"
      :overrides="[
        undefined,
        {
          label: t('breadcrumb.items.references.label'),
          to: localePath({ name: 'references' }),
        },
        undefined,
      ]"
    />
    <section class="container max-w-[1180px] py-16">
      <div class="px-4">
        <div class="mb-12 flex flex-col gap-10">
          <div v-if="reference?.thumbnailImage">
            <p>
              <img
                loading="lazy"
                :src="
                  getResourceUrl(
                    reference?.thumbnailImage?.url + '/1920x1920/FIT'
                  )
                "
              />
            </p>
          </div>
        </div>
        <div
          class="content-reference mx-auto w-full lg:w-3/4"
          v-html="reference?.content"
        />
      </div>
    </section>
    <section class="py-16">
      <div class="mx-auto px-4">
        <h2 class="caption mb-8 text-center">
          {{ t('base.references.gallery') }}
        </h2>
        <UiGalleryCarousel
          v-if="(reference?.photos?.length || 0) > 0"
          :images="reference?.photos ?? []"
        />
      </div>
    </section>
    <section class="py-16">
      <div class="mx-auto px-4">
        <div class="text-center">
          <h2 class="caption mb-8">{{ t('base.references.usedProducts') }}</h2>
          <div class="flex justify-center">
            <UiProductCarousel
              v-if="productImages.length > 0"
              :product-images="productImages"
              show-label
              center-single-image
            />
          </div>
        </div>
      </div>
    </section>
    <section class="py-16">
      <div class="container">
        <h2 class="caption mb-8 text-center">
          {{ t('base.references.moreReferences') }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2">
          <template v-if="loadingMore">
            <UiSkeletonImage v-for="i in 2" :key="i" class="mb-8 w-full px-2" />
          </template>
          <template v-else>
            <ReferencesCard
              v-for="otherReference in moreReferences.items"
              :key="otherReference.nid"
              class="mb-8 w-full px-2"
              :reference="otherReference"
            />
          </template>
        </div>
      </div>
    </section>
  </main>
</template>

<style>
.content-reference p:first-of-type {
  font-weight: bolder;
}

.content-reference p {
  margin-bottom: 2rem;
}
</style>
